// View-selection persistence helpers for the Unified Calendar (SH-1409).
//
// The selected view is stored per-user in the existing `GeneralPreference` KV
// (Type `UnifiedCalendarView`, Value one of `month|week|day`) — the same infra
// the map's `CollisionDetectionEnabled` toggle uses. These pure helpers derive
// the initial view from the loaded preference rows so the resolution logic is
// unit-tested without React or Redux; the React wiring lives in
// `hooks/useCalendarViewPreference` and `CalendarPage`.

import { IUserPreferenceGeneralListItemType } from '@user/store/userPreferences/userPreferencesGeneral'
import type { IUserPreferenceGeneralListItem } from '@user/store/userPreferences/userPreferencesGeneral'
import type { CalendarView } from './typings'

/** SH-1409 AC: the calendar is defaulted to Week on initial load. */
export const DEFAULT_CALENDAR_VIEW: CalendarView = 'week'

const CALENDAR_VIEWS: readonly CalendarView[] = ['month', 'week', 'day']

/** Narrow an arbitrary stored string to a known `CalendarView` token. */
export function isCalendarView(value: string): value is CalendarView {
  return (CALENDAR_VIEWS as readonly string[]).includes(value)
}

/**
 * The user's persisted calendar view, or `undefined` when there is nothing
 * usable to restore — mirrors `getPreferredCollisionDetectionEnabled`.
 *
 * `undefined` covers BOTH "no preference saved yet" (EC-2, first-ever user) and
 * "saved value is an unknown/legacy token" (EC-1, e.g. `agenda`). In either case
 * the caller falls back to `DEFAULT_CALENDAR_VIEW` and surfaces no error, and no
 * preference is written until the user actively toggles.
 */
export function getPreferredCalendarView(
  userGeneralPreferences?: IUserPreferenceGeneralListItem[],
): CalendarView | undefined {
  const rawValue = userGeneralPreferences?.find(
    (row) =>
      row.type === IUserPreferenceGeneralListItemType.UnifiedCalendarView,
  )?.value

  if (rawValue !== undefined && isCalendarView(rawValue)) return rawValue
  return undefined
}
